import { IconUpload } from "@tabler/icons-react";
import { Extension } from "@tiptap/core";
import { defaultSettings, ImagePluginSettings, imagePlugin } from "prosemirror-image-plugin";
import { Node, Schema } from "prosemirror-model";

import { FileInput, FileInputProps } from "~/ui";

import styles from "./ImageUploader.module.css";

export { startImageUpload } from "prosemirror-image-plugin";

export const ImageUploader = Extension.create<ImagePluginSettings>({
  name: "imageUploader",

  addOptions() {
    return {
      ...defaultSettings,
    };
  },

  // biome-ignore-start lint/suspicious/noTsIgnore: carried over from the ESLint suppression
  // @ts-ignore
  onBeforeCreate({ editor }) {
    // biome-ignore-end lint/suspicious/noTsIgnore: carried over from the ESLint suppression
    editor.schema = new Schema({
      nodes: ourUpdateImageNode(editor.schema.spec.nodes, this.options),
      marks: editor.schema.spec.marks,
    });
  },

  addProseMirrorPlugins() {
    return [imagePlugin(this.options)];
  },
});

const imageUploadMimeTypes = [
  "image/apng",
  "image/bmp",
  "image/gif",
  "image/heic",
  "image/heic-sequence",
  "image/heif",
  "image/heif-sequence",
  "image/jpeg",
  "image/png",
  "image/svg+xml",
  "image/tiff",
  "image/tiff-fx",
  "image/webp",
].join(",");

export const ImageUploadControl = (props: FileInputProps) => {
  return (
    <FileInput
      size="xs"
      placeholder="Insert Image"
      icon={<IconUpload size="12" />}
      accept={imageUploadMimeTypes}
      {...props}
      value={null}
      iconClassName={styles.icon}
      placeholderClassName={styles.placeholder}
      inputClassName={styles.input}
    />
  );
};

const ourUpdateImageNode = (
  nodes: Schema["spec"]["nodes"],
  // Additional attributes where the keys are attribute names and values are default values
  pluginSettings: ImagePluginSettings,
): typeof nodes => {
  const { extraAttributes } = pluginSettings;
  const attributesUpdate = Object.keys(extraAttributes)
    .map(attrKey => ({
      [attrKey]: {
        default: extraAttributes[attrKey] || null,
      },
    }))
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  const attributeKeys = [...Object.keys(extraAttributes), "src", "alt"];
  // biome-ignore-start lint/suspicious/noTsIgnore: carried over from the ESLint suppression
  // @ts-ignore
  return nodes.update("image", {
    // biome-ignore-end lint/suspicious/noTsIgnore: carried over from the ESLint suppression
    ...(pluginSettings.hasTitle ? { content: "inline*" } : {}),
    attrs: {
      src: { default: null },
      alt: { default: null },
      height: { default: null },
      width: { default: null },
      maxWidth: { default: null },
      ...attributesUpdate,
    },
    atom: true,
    ...(pluginSettings.isBlock ? { group: "block" } : { group: "inline", inline: true }),
    draggable: true,
    toDOM(node: Node) {
      const toAttributes = attributeKeys
        .map(attrKey => ({
          [`imageplugin-${attrKey}`]: node.attrs[attrKey],
        }))
        // merge
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});
      return [
        "div",
        {
          class: "imagePluginRoot",
          ...toAttributes,
        },
        [
          "img",
          {
            ...attributeKeys.map(k => ({ [`${k}`]: node.attrs[k] })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
          },
        ],
      ];
    },
    parseDOM: [
      {
        tag: "div.imagePluginRoot",
        getAttrs(dom) {
          if (typeof dom === "string") {
            return {};
          }
          return (
            attributeKeys
              .map(attrKey => ({
                [attrKey]: dom.getAttribute(`imageplugin-${attrKey}`),
              }))
              // merge
              .reduce((acc, curr) => ({ ...acc, ...curr }), {})
          );
        },
      },
    ],
  });
};
